import {
  type AltchaWidgetElement,
  makeAltchaBoxClickable,
  setupAltcha,
  waitForAltcha,
} from "../../utils/altcha";
import {
  type CodeInputController,
  type OtpErrorMessages,
  requestLoginCode,
  setupCodeInput,
  verifyLoginCode,
} from "../../utils/otp";

export const initWorkSignup = () => {
  const workSignup = document.getElementById("work-signup") as HTMLDivElement;

  if (!workSignup) {
    return;
  }

  const emailInput = workSignup.querySelector(".work-signup-email") as HTMLInputElement | null;

  if (!emailInput) {
    return;
  }

  setupAltcha();

  const form = workSignup.querySelector("#work-signup-form") as HTMLFormElement;
  const stepForm = workSignup.querySelector(".step-form") as HTMLDivElement;
  const stepCode = workSignup.querySelector(".step-code") as HTMLDivElement;
  const csrfRequestCode = (workSignup.querySelector(".csrf-request-code") as HTMLInputElement)
    .value;
  const csrfVerifyCode = (workSignup.querySelector(".csrf-verify-code") as HTMLInputElement).value;

  const altchaWrapper = workSignup.querySelector(".altcha-wrapper") as HTMLDivElement;
  const altchaWidget = workSignup.querySelector(
    ".altcha-wrapper altcha-widget",
  ) as AltchaWidgetElement;
  makeAltchaBoxClickable(altchaWidget);

  const submitButton = workSignup.querySelector('button[type="submit"]') as HTMLButtonElement;
  const submitButtonLabel = submitButton.innerHTML;

  let currentEmail = "";

  // Prevent native form validation from triggering on the hidden altcha input
  submitButton.type = "button";

  submitButton.addEventListener("click", () => {
    submitButton.disabled = true;

    waitForAltcha(altchaWrapper, altchaWidget)
      .then((payload) => {
        if (!form.checkValidity()) {
          form.reportValidity();
          submitButton.disabled = false;
          return;
        }

        currentEmail = emailInput.value;
        sendCodeRequest(payload);
      })
      .catch(() => {
        submitButton.disabled = false;
        alert(errorMessages.generic);
      });
  });

  function getSpamFields() {
    const spamvalidation = workSignup.querySelector(
      'input[name="spamvalidation"]',
    ) as HTMLInputElement | null;
    const yourLastName = workSignup.querySelector(
      'input[name="your_last_name"]',
    ) as HTMLInputElement | null;
    return {
      spamvalidation: spamvalidation?.value || "",
      yourLastName: yourLastName?.value || "",
    };
  }

  function sendCodeRequest(payload: string) {
    requestLoginCode(currentEmail, csrfRequestCode, payload, getSpamFields(), errorMessages)
      .then((result) => {
        submitButton.disabled = false;

        if (!result.success) {
          alert(result.errorMessage);
          return;
        }

        showCodeStep();
      })
      .catch(() => {
        submitButton.disabled = false;
      });
  }

  // Code step elements
  const codeInputWrapper = stepCode.querySelector(".code-input-wrapper") as HTMLDivElement;
  const codeSpinner = stepCode.querySelector(".code-spinner") as HTMLDivElement;
  const codeError = stepCode.querySelector(".code-error") as HTMLDivElement;
  const codeErrorMessage = stepCode.querySelector(".code-error-message") as HTMLDivElement;
  const errorMessages: OtpErrorMessages = {
    generic: stepCode.dataset.genericError || "Something went wrong.",
    rateLimited: stepCode.dataset.rateLimitError || "Too many requests.",
  };
  let isVerifying = false;

  const codeInput: CodeInputController = setupCodeInput(
    codeInputWrapper,
    (code) => {
      if (!isVerifying) {
        submitCode(code);
      }
    },
    () => {
      codeError.classList.add("d-none");
    },
  );

  function showCodeStep() {
    stepForm.classList.add("d-none");
    stepCode.classList.remove("d-none");
    const codeDescription = stepCode.querySelector(".code-description") as HTMLParagraphElement;
    codeDescription.textContent = codeDescription.textContent
      ? codeDescription.textContent.replace(/\S+@\S+|(?=\.)/, currentEmail)
      : `We sent a code to ${currentEmail}.`;

    resetCodeInput();
  }

  function submitCode(code: string) {
    isVerifying = true;
    codeInput.disable();
    codeSpinner.classList.remove("d-none");
    codeError.classList.add("d-none");

    verifyLoginCode(currentEmail, code, csrfVerifyCode, errorMessages)
      .then((result) => {
        if (result.success) {
          // User is now authenticated — submit the original form
          form.submit();
          return;
        }

        codeErrorMessage.textContent = result.errorMessage || errorMessages.generic;
        codeError.classList.remove("d-none");
        resetCodeInput();
      })
      .catch(() => {
        codeErrorMessage.textContent = errorMessages.generic;
        codeError.classList.remove("d-none");
        resetCodeInput();
      });
  }

  function resetCodeInput() {
    isVerifying = false;
    codeSpinner.classList.add("d-none");
    codeInput.reset();
  }

  // Back button
  const backButton = stepCode.querySelector(".back") as HTMLButtonElement;
  backButton.addEventListener("click", () => {
    stepCode.classList.add("d-none");
    stepForm.classList.remove("d-none");
    submitButton.disabled = false;
    submitButton.innerHTML = submitButtonLabel;
  });

  // Resend code
  const resendCodeButton = stepCode.querySelector(".resend-code") as HTMLButtonElement;
  const resendAltchaWrapper = stepCode.querySelector(".resend-altcha-wrapper") as HTMLDivElement;
  const resendAltchaWidget = resendAltchaWrapper.querySelector(
    "altcha-widget",
  ) as AltchaWidgetElement;
  makeAltchaBoxClickable(resendAltchaWidget);

  resendCodeButton.addEventListener("click", () => {
    resendCodeButton.disabled = true;

    waitForAltcha(resendAltchaWrapper, resendAltchaWidget)
      .then((payload) => sendResendCodeRequest(payload))
      .catch(() => {
        resendCodeButton.disabled = false;
        alert(errorMessages.generic);
      });
  });

  function sendResendCodeRequest(payload: string) {
    requestLoginCode(currentEmail, csrfRequestCode, payload, getSpamFields(), errorMessages)
      .then((result) => {
        resendCodeButton.disabled = false;

        if (!result.success) {
          alert(result.errorMessage);
          return;
        }

        resetCodeInput();

        const successMessage = resendCodeButton.dataset.successMessage;
        if (successMessage) {
          alert(successMessage);
        }
      })
      .catch(() => {
        resendCodeButton.disabled = false;
        alert(errorMessages.generic);
      });
  }
};
